import { useDispatch, useSelector } from 'react-redux';
import {
  Quote,
  selectClient,
  selectSession,
  selectSessionAdvisor,
  selectIllustration,
  putMultipleFundingSourceForClientByClientIdRequest,
} from '@client/model';
import { SessionType } from '@shared';
import {
  createPolicyFundingSourceFromQuote,
  createUpdatedQuoteFromResponse,
  createQuoteWithROIandProjectedCoverage,
} from '@/util/policyQuoteCalculations';
import {
  deleteJointPolicyFromBothClients,
  findPartnerPolicyFundingSourceId,
} from '@/util/findPartnerPolicyFundingSourceId';

interface QuoteWithOriginalROI extends Quote {
  originalROIValues?: {
    projectedReturnOnInvestment?: number;
    projectedCompoundAnnualGrowthRate?: number;
    projectedInternalRateOfReturn?: number;
    investmentPeriodYears?: number;
  };
}

interface UsePolicyManagementProps {
  quote: Quote;
  selectedTab?: ShopInsuranceTab;
  clientPartner?: Client | null;
  fundingSourceId?: string | null;
  onClose?: (extraParams?: Record<string, string>) => void;
}

export function usePolicyManagement({
  quote,
  selectedTab = 'single',
  clientPartner = null,
  fundingSourceId = null,
  onClose,
}: UsePolicyManagementProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const illustration = useSelector(selectIllustration);
  const existingUpdatedQuote = quote.updatedQuote || null;
  const illustrationPdfUrl = quote.illustrationPdfUrl || null;

  // Check if there's a completed illustration for this quote
  const quoteId = quote.insuranceQuoteId;
  const completedIllustration = illustration.completedIllustrations?.[quoteId];

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const handleAddPolicy = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!client) return;

    // Determine which quote to use and what data to store
    let quoteToUse = quote;
    let updatedQuoteForStorage: Quote | null = null;
    let finalIllustrationPdfUrl = illustrationPdfUrl;

    // Check for completed illustration first (highest priority)
    if (completedIllustration?.responseData && completedIllustration?.downloaded) {
      // Update quote with illustration response data
      const updatedQuote = createUpdatedQuoteFromResponse(
        quote,
        completedIllustration.responseData,
      );

      // Preserve original ROI values if they exist
      const originalROI = (quote as QuoteWithOriginalROI).originalROIValues;

      if (
        originalROI &&
        originalROI.projectedReturnOnInvestment !== undefined &&
        originalROI.projectedCompoundAnnualGrowthRate !== undefined &&
        originalROI.investmentPeriodYears !== undefined
      ) {
        updatedQuoteForStorage = {
          ...updatedQuote,
          roiValues: {
            projectedReturnOnInvestment: originalROI.projectedReturnOnInvestment,
            projectedCompoundAnnualGrowthRate: originalROI.projectedCompoundAnnualGrowthRate,
            projectedInternalRateOfReturn: originalROI.projectedInternalRateOfReturn,
            investmentPeriodYears: originalROI.investmentPeriodYears,
          },
        } as Quote;
      } else {
        // Recalculate ROI with updated values
        updatedQuoteForStorage = createQuoteWithROIandProjectedCoverage(updatedQuote, client);
      }

      quoteToUse = updatedQuote;
      finalIllustrationPdfUrl = completedIllustration.pdfUrl;
    } else if ((quote as QuoteWithOriginalROI).originalROIValues) {
      // Quote already has ROI values from the shop (adjusted quote)
      // Use it as-is, preserving the adjusted premium and calculated ROI
      quoteToUse = quote;
      updatedQuoteForStorage = quote;
    } else if (existingUpdatedQuote) {
      // Use existing updated quote
      quoteToUse = existingUpdatedQuote;
      updatedQuoteForStorage = existingUpdatedQuote;
    } else {
      // No ROI values present - this shouldn't happen from the shop but handle it
      updatedQuoteForStorage = createQuoteWithROIandProjectedCoverage(quote, client);
    }

    // Create funding source using the appropriate quote (original or updated)
    const finalPolicy = createPolicyFundingSourceFromQuote(
      quoteToUse,
      client,
      isAdvisorSession,
      advisor?.advisorId,
      updatedQuoteForStorage,
      finalIllustrationPdfUrl,
    );

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: finalPolicy,
        fundingSourceId: null,
      }),
    );

    if (selectedTab === 'joint' && quote.policyType === 'Joint Life' && clientPartner) {
      const partnerFinalPolicy = createPolicyFundingSourceFromQuote(
        quoteToUse,
        clientPartner,
        isAdvisorSession,
        advisor?.advisorId,
        updatedQuoteForStorage,
        finalIllustrationPdfUrl,
      );

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: clientPartner.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: partnerFinalPolicy,
          fundingSourceId: null,
        }),
      );
    }

    if (onClose) {
      onClose({ downloadReport: 'true' });
    }
  };

  const deletePolicyForClientOnly = () => {
    if (!client || !fundingSourceId) return;

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: null,
        fundingSourceId,
      }),
    );
  };

  const deletePolicyForBothClients = () => {
    if (!client || !fundingSourceId) return;

    deleteJointPolicyFromBothClients(
      dispatch,
      client,
      fundingSourceId,
      putMultipleFundingSourceForClientByClientIdRequest,
    );
  };

  const handleRemovePolicy = (event: React.MouseEvent, onShowJointDialog: () => void) => {
    event.stopPropagation();

    if (!client || !fundingSourceId) return;

    if (
      selectedTab === 'joint' &&
      quote.policyType === 'Joint Life' &&
      clientPartner &&
      quote.insuranceQuoteId
    ) {
      const partnerFundingSourceId = findPartnerPolicyFundingSourceId(clientPartner, {
        insuranceQuoteId: quote.insuranceQuoteId,
      });

      if (partnerFundingSourceId) {
        onShowJointDialog();
      } else {
        deletePolicyForClientOnly();
      }
    } else {
      deletePolicyForClientOnly();
    }
  };

  return {
    handleAddPolicy,
    handleRemovePolicy,
    deletePolicyForClientOnly,
    deletePolicyForBothClients,
  };
}